import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  AdminPanelSettings as AdminIcon,
  Block as BlockIcon,
  CheckCircleOutline as ReactivateIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { User, Role } from '../../types/auth';
import { useCreateUser, useDeleteUser, useSetUserActive, useUsers } from '../../api/admin';
import { useSSOSetting } from '../../api/sso';
import { requestMessage } from '../../utils/requestError';
import { useAuth } from '../../contexts/AuthContext';
import AdminPageHeader from './AdminPageHeader';
import PersonDrawer, { ConfirmDialog, linkedOrganisationName, signInLabel } from './PersonDrawer';

interface UsersManagementProps {
  roles: Role[];
}

interface FormErrors {
  email?: string;
  username?: string;
  password?: string;
  role_ids?: string;
  non_field_errors?: string;
}

const EMPTY_FORM = {
  email: '',
  username: '',
  password: '',
  role_ids: [] as number[],
  is_staff: false,
};

/**
 * The People page: everyone who can use Mitigate in this organisation.
 * Rendered for both `/admin/people` and `/admin/people/:userId`; the second
 * form opens the PersonDrawer for that person, and closing it returns to the
 * first. Adding a person is a small dialog; everything else happens in the
 * drawer.
 */
export default function UsersManagement({ roles }: UsersManagementProps) {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { userId: userIdParam } = useParams<{ userId?: string }>();
  const openUserId = userIdParam && Number.isFinite(Number(userIdParam)) ? Number(userIdParam) : null;

  const [addOpen, setAddOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  // How a new person will sign in. Defaults to single sign-on once it is on.
  const [signInMethod, setSignInMethod] = useState<'password' | 'sso'>('password');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [pendingDeactivate, setPendingDeactivate] = useState<User | null>(null);
  const [pendingDelete, setPendingDelete] = useState<User | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: users = [], isLoading } = useUsers();
  const createUser = useCreateUser();
  const deleteUser = useDeleteUser();
  const setUserActive = useSetUserActive();
  const { data: ssoSetting } = useSSOSetting();
  const ssoOn = !!ssoSetting?.provider?.enabled;

  const openPerson = (id: number) => navigate(`/admin/people/${id}`);
  const closePerson = () => navigate('/admin/people');

  const handleAddOpen = () => {
    setFormData(EMPTY_FORM);
    setSignInMethod(ssoOn ? 'sso' : 'password');
    setFormErrors({});
    setAddOpen(true);
  };

  const handleAddClose = () => {
    setAddOpen(false);
    setFormData(EMPTY_FORM);
    setFormErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});

    try {
      const viaSso = signInMethod === 'sso';
      await createUser.mutateAsync({
        email: formData.email,
        username: formData.username,
        // SSO users get no password: they can only sign in through the IdP.
        ...(viaSso ? {} : { password: formData.password }),
        auth_source: viaSso ? 'sso' : 'password',
        role_ids: formData.role_ids,
        // Extra permissions are granted later, from the person's drawer.
        capability_ids: [],
        is_staff: formData.is_staff,
      });
      handleAddClose();
    } catch (error: unknown) {
      const err = error as { data?: Record<string, unknown>; message?: string };
      const fallback = err.message || 'Something went wrong. Please try again.';
      if (!err.data) {
        setFormErrors({ non_field_errors: fallback });
        return;
      }
      // Field errors from the backend, one message per field.
      const newErrors: FormErrors = {};
      Object.entries(err.data).forEach(([key, value]) => {
        let message: string;
        if (Array.isArray(value)) {
          message = String(value[0]);
        } else if (value && typeof value === 'object') {
          message = String(Object.values(value as Record<string, unknown>)[0]);
        } else {
          message = String(value);
        }
        if (key === 'detail') {
          newErrors.non_field_errors = message;
        } else {
          newErrors[key as keyof FormErrors] = message;
        }
      });
      setFormErrors(Object.keys(newErrors).length > 0 ? newErrors : { non_field_errors: fallback });
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!pendingDeactivate) return;
    try {
      await setUserActive.mutateAsync({ userId: pendingDeactivate.id, isActive: false });
    } catch (error: unknown) {
      setActionError(requestMessage(error, 'Could not deactivate this person.'));
    } finally {
      setPendingDeactivate(null);
    }
  };

  const handleReactivate = async (user: User) => {
    try {
      await setUserActive.mutateAsync({ userId: user.id, isActive: true });
    } catch (error: unknown) {
      setActionError(requestMessage(error, 'Could not reactivate this person.'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteUser.mutateAsync(String(pendingDelete.id));
      if (openUserId === pendingDelete.id) closePerson();
    } catch (error: unknown) {
      setActionError(requestMessage(error, 'Could not delete this person.'));
    } finally {
      setPendingDelete(null);
    }
  };

  const sortedUsers = React.useMemo(() => {
    return [...users].sort((a, b) => {
      // Administrators first, then by name.
      if (a.is_staff !== b.is_staff) {
        return a.is_staff ? -1 : 1;
      }
      return a.username.localeCompare(b.username);
    });
  }, [users]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <AdminPageHeader
        title="People"
        description="Who can use Mitigate in your organisation, what they can do, and how they sign in."
        audience="admins"
        action={
          <Button variant="contained" color="primary" onClick={handleAddOpen}>
            Add person
          </Button>
        }
      />

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Roles</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Access</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Sign-in</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((user) => {
              const isSelf = user.id === currentUser?.id;
              const isLinked = user.auth_source === 'linked';
              const isActive = user.is_active !== false;
              const linkedOrg = linkedOrganisationName(user);
              return (
                <TableRow
                  key={user.id}
                  hover
                  selected={user.id === openUserId}
                  onClick={() => openPerson(user.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        opacity: isActive ? 1 : 0.55,
                      }}
                    >
                      {user.is_staff && <AdminIcon color="primary" fontSize="small" />}
                      <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                        {user.username}
                      </Typography>
                      {isSelf && <Chip size="small" label="You" />}
                      {isLinked && (
                        <Tooltip title="Works here from another organisation's account. Their details and roles are managed there.">
                          <Chip
                            size="small"
                            variant="outlined"
                            label={linkedOrg ? `Linked from ${linkedOrg}` : 'Linked from another organisation'}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.roles.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        None
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {user.roles.map((role) => (
                          <Chip key={role.id} size="small" label={role.name} />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.is_staff ? (
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                        Administrator
                      </Typography>
                    ) : (
                      <Typography variant="body2">Member</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {signInLabel(user)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {isActive ? (
                      <Chip size="small" color="success" variant="outlined" label="Active" />
                    ) : (
                      <Chip size="small" label="Deactivated" />
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Open">
                      <IconButton size="small" onClick={() => openPerson(user.id)} aria-label={`Open ${user.username}`}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!isSelf && (
                      <>
                        <Tooltip title={isActive ? 'Deactivate' : 'Reactivate'}>
                          <IconButton
                            size="small"
                            onClick={() => (isActive ? setPendingDeactivate(user) : handleReactivate(user))}
                            disabled={setUserActive.isPending}
                            aria-label={isActive ? `Deactivate ${user.username}` : `Reactivate ${user.username}`}
                          >
                            {isActive ? <BlockIcon fontSize="small" /> : <ReactivateIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        {!isLinked && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setPendingDelete(user)}
                              disabled={deleteUser.isPending}
                              aria-label={`Delete ${user.username}`}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {sortedUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No one here yet. Add the first person to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PersonDrawer userId={openUserId} roles={roles} onClose={closePerson} />

      <Dialog open={addOpen} onClose={handleAddClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add person</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!formErrors.email}
              helperText={formErrors.email}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              error={!!formErrors.username}
              helperText={formErrors.username}
              margin="normal"
            />
            {ssoOn && (
              <FormControl margin="normal">
                <Typography variant="subtitle2">Signs in with</Typography>
                <RadioGroup
                  row
                  value={signInMethod}
                  onChange={(e) => setSignInMethod(e.target.value as 'password' | 'sso')}
                >
                  <FormControlLabel value="sso" control={<Radio />} label="Single sign-on" />
                  <FormControlLabel value="password" control={<Radio />} label="A password" />
                </RadioGroup>
              </FormControl>
            )}
            {signInMethod === 'password' && (
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                error={!!formErrors.password}
                helperText={formErrors.password}
                margin="normal"
              />
            )}
            <FormControl fullWidth margin="normal" error={!!formErrors.role_ids}>
              <InputLabel id="roles-label">Roles</InputLabel>
              <Select
                labelId="roles-label"
                multiple
                value={formData.role_ids}
                onChange={(e) => setFormData({ ...formData, role_ids: e.target.value as number[] })}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) =>
                  roles
                    .filter((role) => selected.includes(role.id))
                    .map((role) => role.name)
                    .join(', ')
                }
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Checkbox checked={formData.role_ids.includes(role.id)} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={formData.is_staff}
                  onChange={(e) => setFormData({ ...formData, is_staff: e.target.checked })}
                />
              }
              label="Administrator (can manage people and settings)"
            />
            {formErrors.non_field_errors && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {formErrors.non_field_errors}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={createUser.isPending}>
            {createUser.isPending ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={pendingDeactivate !== null}
        title={`Deactivate ${pendingDeactivate?.username ?? ''}?`}
        body="They are signed out everywhere and can't sign in, including through single sign-on, until you reactivate them."
        confirmLabel="Deactivate"
        destructive
        busy={setUserActive.isPending}
        onConfirm={handleConfirmDeactivate}
        onCancel={() => setPendingDeactivate(null)}
      />
      <ConfirmDialog
        open={pendingDelete !== null}
        title={`Delete ${pendingDelete?.username ?? ''}?`}
        body="Their account and settings are removed for good. This can't be undone."
        confirmLabel="Delete"
        destructive
        busy={deleteUser.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Box>
  );
}
